import React, { useEffect } from 'react';

import { ROUTES } from '@elara/client-core/constants';

import { Logo } from '#ui/index.js';
import LoginSlideshow from '#ui/login-slideshow.js';

interface SessionLayoutProps {
  children: React.ReactNode;
  title: string;
  footer?: React.ReactNode;
}

export function SessionLayout({ children, title, footer }: SessionLayoutProps) {
  useEffect(() => {
    document.body.style.backgroundColor = 'var(--color-background-secondary)';
  }, []);

  return (
    // The slideshow is `absolute inset-0` inside its pane, so it has no height of its own — it takes
    // the row's, and the row takes the taller of the two columns. A long form (the membership
    // payment step) would therefore stretch the photo past the fold.
    //
    // The photo is held at one viewport by making its pane sticky, NOT by capping the row and
    // scrolling the content column inside it. Both pin the photo, but an inner scroller only
    // responds to a wheel that is over it — scrolling with the pointer on the photo would do
    // nothing. Sticky leaves everything on the document scroller, so a wheel anywhere on the page
    // scrolls the form, and the keyboard, scrollbar, and mobile gestures keep working for free.
    //
    // `h-screen` also gives the pane a definite cross-size, which is what stops the flex row
    // stretching it; `self-start` keeps it at the top once it is no longer stretched. Nothing
    // between here and the document may set `overflow`, or the stickiness silently dies.
    <div className="flex min-h-screen bg-background-secondary safe-area-top">
      <div className="w-full md:max-w-xl flex flex-col justify-between bg-background-secondary md:px-10 md:py-10 px-6 py-8 space-y-20">
        <header>
          <a href={ROUTES.LOGIN}>
            <Logo />
          </a>
        </header>

        <div className="space-y-6">
          <h1 className="text-2xl lg:text-4xl font-display font-light mb-12">{title}</h1>
          {children}
        </div>

        <footer>
          {footer && (
            <div className="text-center px-4 py-4 text-sm rounded-xs bg-muted text-secondary-foreground">{footer}</div>
          )}
        </footer>
      </div>
      <div className="hidden md:block md:sticky md:top-0 md:self-start md:h-screen relative w-full overflow-hidden">
        <LoginSlideshow />
      </div>
    </div>
  );
}
